import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useRedtailFamilyContext } from '../../../../../api/redtail'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import presets from './presets'

export default function useRedtailMetricPreset (presetKey, config = {}) {
  const { clientId, userId } = useAppContext()
  const familyContext = useRedtailFamilyContext(clientId)

  const preset = presets[presetKey]
  const isUserScoped = config.scope === 'user'

  const metricQuery = useQuery({
    queryKey: [QUERY_KEYS.redtailDomainQuery, 'metric-preset', userId, clientId, presetKey, config.scope, config.categoryIds, config.dueWithinDays],
    queryFn: async () => {
      const members = isUserScoped ? null : familyContext.members
      const rawData = await preset.fetchData(members, clientId, config)
      const result = preset.compute(rawData, config)
      return Array.isArray(result) ? result : { value: result }
    },
    enabled: isUserScoped
      ? !!preset
      : !!preset && familyContext.linked === true && !!familyContext.members?.length
  })

  const queryData = metricQuery.data
  const items = Array.isArray(queryData) ? queryData : null
  const value = items ? items.length : (queryData?.value ?? queryData ?? null)

  const merged = useMemo(() => {
    if (!preset) return { title: 'Unknown Preset', label: '', icon: null, format: '0' }
    return {
      title: config.title ?? preset.defaults.title,
      label: config.hideLabel ? null : (config.label ?? preset.defaults.label),
      icon: config.icon ?? preset.defaults.icon,
      format: config.format ?? preset.defaults.format
    }
  }, [preset, config.title, config.label, config.hideLabel, config.icon, config.format])

  return {
    value,
    items,
    members: familyContext.members,
    DetailPanel: preset?.DetailPanel,
    isLoading: isUserScoped
      ? metricQuery.isFetching
      : familyContext.isLoading || metricQuery.isFetching,
    notLinked: isUserScoped ? false : familyContext.linked === false,
    error: isUserScoped
      ? metricQuery.error
      : familyContext.error || metricQuery.error,
    ...merged
  }
}
